import { LoginTranslation } from '../../utils/helper.type';
import BackArrow from '../../assets/back_arrow.png';
import { Trans } from 'react-i18next';
import { renderEmailInput, renderSubmitButton } from '../login/loginHelper';
import TabletBackButton from '../../component/tabletBackButton/tabletBackButton';
import TabletLayoutImage from '../../component/tabletLayoutImage/tabletLayoutImage';
import DesktopLayoutImage from '../../component/desktopLayoutmage/desktopLayoutImage';
import RenderInPlayLogo from '../../component/inPlayLogo/inPlayLogo';

/**
 * Renders the "Forgot Email" form used on the Forgot Password screen.
 *
 * This function displays a description, an email input field, and a submit button.
 * It's designed to collect the user's registered email and trigger a password reset link.
 *
 * @param email - The current value of the email input.
 * @param setEmail - A function to update the email input state.
 * @param handleSendLink - A function that handles the form submission (e.g., sending the reset link).
 * @param isDisabled - A boolean to disable the submit button (e.g., if email is invalid or empty).
 * @param isLoading - A boolean that shows a loading state on the submit button.
 * @param loginTranslation - An object containing translated label text for the email field.
 *
 * @returns A JSX element containing the form layout.
 */
export const renderForgotEmailForm = (
  email: string,
  setEmail: (val: string) => void,
  handleSendLink: (e: React.FormEvent) => void,
  isDisabled: boolean,
  isLoading: boolean,
  loginTranslation: LoginTranslation,
) => {
  return (
    <>
      <p
        className="mb-12 text-[#1A1A1A] text-lg"
        data-testid="forgot-email-description"
      >
        <Trans i18nKey="login.registerEmailDecs" />
      </p>
      <form onSubmit={handleSendLink} data-testid="forgot-email-form">
        {renderEmailInput(
          <Trans i18nKey="login.email" />,
          email,
          setEmail,
          loginTranslation.email,
        )}

        <div className="mt-20">
          {renderSubmitButton(
            isDisabled,
            isLoading,
            <Trans i18nKey="forgotPassword.sendlink" />,
          )}
        </div>
      </form>
    </>
  );
};
/**
 * Renders the desktop view for the Forgot Password page.

 * @param handleSubmit - Function to handle form submission
 * @param email - Current email state value
 * @param setEmail - Function to update email state
 * @param loginTranslation - Translation object for login fields
 * @param isDisabled - Boolean to disable the submit button
 * @param onClickLogin - Function to navigate back to login page
 * @returns JSX.Element
 */
export const renderDesktopView = (
  handleSendLink: (e: React.FormEvent) => void,
  email: string,
  setEmail: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation: LoginTranslation,
  isDisabled: boolean,
  onClickLogin: () => void,
  isLoading: boolean,
) => {
  return (
    <div
      className="lg:flex h-screen w-full font-helvetica"
      data-testid="desktop-view-container"
    >
      <div className="flex flex-col flex-1 px-10 py-6">
        <div
          onClick={onClickLogin}
          className="text-base flex gap-4 items-center mb-8 cursor-pointer justify-start"
          data-testid="back-button"
        >
          <img src={BackArrow} alt="Back Arrow" className="w-5 h-4" />
          <span>
            <Trans i18nKey="login.backTo" />{' '}
            <span onClick={onClickLogin} className="underline text-black">
              <Trans i18nKey="login.title" />
            </span>
          </span>
        </div>

        <div className="flex flex-col max-w-[520px] w-full mx-auto">
          <div className="mt-[200px]">
            <RenderInPlayLogo />
            <div className="login-header-container mb-[50px]">
              <h1
                className="text-6xl text-[#1A1A1A] mb-6"
                data-testid="forgot-password-header"
              >
                <Trans i18nKey="forgotPassword.forgot" />
                <br />
                <Trans i18nKey="forgotPassword.passwordtext" />
              </h1>
            </div>
            <div data-testid="forgot-email-form-wrapper">
              {renderForgotEmailForm(
                email,
                setEmail,
                handleSendLink,
                isDisabled,
                isLoading,
                loginTranslation,
              )}
            </div>
          </div>
        </div>
      </div>

      <DesktopLayoutImage data-testid="desktop-layout-image" />
    </div>
  );
};

/**
 * Renders the tablet view layout for the "Forgot Password" screen.
 *
 * This layout includes:
 * - A full-screen background image with a dark overlay
 * - A back button component (`TabletBackButton`)
 * - A header title using a dynamic i18n translation key
 * - A centered form for entering the user's email address to receive a reset link
 *
 * @param handleBackToLogin - Callback function triggered when the back button is clicked.
 * @param email - The current email input value.
 * @param setEmail - A React state setter function to update the email value.
 * @param handleSendLink - Form submit handler that sends the reset password link.
 * @param loginTranslation - Object containing translated text (e.g. email placeholder).
 * @param passwordMainTitle - i18n key used for rendering the screen title.
 * @param isDisabled - Whether the submit button should be disabled.
 * @param isLoading - Whether the form is in a loading state (e.g. showing a spinner).
 *
 * @returns JSX.Element - The rendered tablet layout for the forgot password screen.
 */
export const renderTabletView = (
  handleBackToLogin: () => void,
  email: string,
  setEmail: React.Dispatch<React.SetStateAction<string>>,
  handleSendLink: (e: React.FormEvent) => void,
  loginTranslation: LoginTranslation,
  passwordMainTitle: string,
  isDisabled: boolean,
  isLoading: boolean,
) => {
  return (
    <div
      className="relative min-h-screen w-full"
      data-testid="tablet-view-container"
    >
      <TabletLayoutImage />

      <div className="absolute inset-0 bg-black/30" data-testid="overlay" />

      <TabletBackButton onClick={handleBackToLogin} data-testid="back-button" />

      <div
        className="tablet-login-container"
        data-testid="tablet-login-container"
      >
        <div className="flex flex-col">
          <div
            className="text-white mt-auto mb-20"
            data-testid="logo-and-title"
          >
            <RenderInPlayLogo
              className={'mb-10 h-10'}
              data-testid="render-logo"
            />
            <h1
              className="text-4xl sm:text-5xl login-font-style mb-2"
              data-testid="password-main-title"
            >
              <Trans i18nKey={passwordMainTitle} />
            </h1>
          </div>
        </div>

        <div className="flex flex-col justify-center items-center h-full">
          <div
            className="tablet-login-form"
            data-testid="forgot-email-form-wrapper"
          >
            {renderForgotEmailForm(
              email,
              setEmail,
              handleSendLink,
              isDisabled,
              isLoading,
              loginTranslation,
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
